import React from 'react'
import { createPortal } from 'react-dom'
import { ZoomIn, ZoomOut } from 'lucide-react'

import { Slider } from '@/components/ui/slider'
import {
  stopPropagation,
  useFloatingChromePosition,
} from '@/components/app/editor/tools/floating-toolbar'

import { PANEL_HEIGHT, PANEL_WIDTH } from './constants/zoom-slider-dimensions'
import { buildZoomedPhotoFill, resolveMaxZoomScale } from './helpers/zoomed-photo-fill'
import { computeZoomSliderPos } from './helpers/zoom-slider-position'
import { useCancellableSliderDrag } from './hooks/use-cancellable-slider-drag'
import type { CropZoomSliderProps, CropZoomSliderRef } from './types/crop-zoom-slider-props'

export type { CropZoomSliderProps, CropZoomSliderRef } from './types/crop-zoom-slider-props'

/** Never below native cover-fit — matches `computePhotoFillOffsetAfterResize`'s own scale floor. */
const MIN_ZOOM_SCALE = 1
const ZOOM_STEP = 0.01

/**
 * Floating zoom slider shown below the shape in crop mode — enlarges/shrinks the photo inside
 * the (fixed) crop frame via `ShapePhotoFill.scale`, without moving the shape's own path/bounds.
 * Panning slack is re-clamped on every change via `computeCropPhotoPan` (zero delta, just a
 * re-clamp against the new scale) so a zoom-out can't leave the pan sitting outside the photo's
 * new, smaller covered size.
 */
export const CropZoomSlider = React.forwardRef<CropZoomSliderRef, CropZoomSliderProps>(
  ({ shape, scale, canvasRef, onScaleChange, onScaleCommit }, ref) => {
    // Declared before the position hook: the imperative handle keeps its original place ahead of
    // the position layout effect.
    const { handleValueChange, handleValueCommit } = useCancellableSliderDrag(ref)
    const pos = useFloatingChromePosition(canvasRef, shape, scale, computeZoomSliderPos)

    const { photoFill } = shape

    if (!pos || !photoFill) return null

    const maxScale = resolveMaxZoomScale(shape, photoFill)

    const applyScale = (nextScale: number, commit: boolean) => {
      const next = buildZoomedPhotoFill(shape, photoFill, nextScale)

      if (commit) {
        onScaleCommit(shape.id, next)
      } else {
        onScaleChange(shape.id, next)
      }
    }

    return createPortal(
      <div
        style={{ position: 'fixed', left: pos.x, top: pos.y, width: PANEL_WIDTH, zIndex: 9999 }}
        onPointerDown={stopPropagation}
        onPointerUp={stopPropagation}
        onClick={stopPropagation}
      >
        <div
          className="flex items-center gap-3 rounded-[14px] border border-black/[0.07] bg-white px-4"
          style={{
            height: PANEL_HEIGHT,
            boxShadow: '0 4px 20px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06)',
          }}
        >
          <ZoomOut className="h-4 w-4 flex-shrink-0 text-[#1a1a1a]/50" />
          <Slider
            value={[photoFill.scale]}
            min={MIN_ZOOM_SCALE}
            max={maxScale}
            step={ZOOM_STEP}
            onValueChange={([value]) => handleValueChange(value, applyScale)}
            onValueCommit={([value]) => handleValueCommit(value, applyScale)}
            aria-label="Photo zoom"
            className="[&_[data-slot=slider-range]]:bg-sky-600 [&_[data-slot=slider-thumb]]:border-sky-600 [&_[data-slot=slider-track]]:bg-sky-600/20"
          />
          <ZoomIn className="h-4 w-4 flex-shrink-0 text-[#1a1a1a]/50" />
        </div>
      </div>,
      document.body
    )
  }
)

CropZoomSlider.displayName = 'CropZoomSlider'
